/**
 * Time Formatting Utilities
 * Human-friendly relative time display
 */

/**
 * Format a date string as a relative time (e.g., "2 hours ago", "Yesterday")
 * @param {string} dateString - ISO date string
 * @returns {string} Human-readable relative time
 */
export const formatRelativeTime = (dateString) => {
  if (!dateString) return '';

  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now - date;
  const diffSecs = Math.floor(diffMs / 1000);
  const diffMins = Math.floor(diffSecs / 60);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  // Just now (less than 1 minute)
  if (diffSecs < 60) {
    return 'Just now';
  }

  // Minutes ago
  if (diffMins < 60) {
    return diffMins === 1 ? '1 min ago' : `${diffMins} mins ago`;
  }

  // Hours ago
  if (diffHours < 24) {
    return diffHours === 1 ? '1 hour ago' : `${diffHours} hours ago`;
  }

  // Yesterday
  if (diffDays === 1) {
    return 'Yesterday';
  }

  // Days ago (up to 7 days)
  if (diffDays < 7) {
    return `${diffDays} days ago`;
  }

  // Older than a week - show formatted date
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined,
  });
};

/**
 * Check if a date is within the last N minutes
 * @param {string} dateString - ISO date string
 * @param {number} minutes - Number of minutes to check
 * @returns {boolean} True if date is within the threshold
 */
export const isWithinMinutes = (dateString, minutes = 5) => {
  if (!dateString) return false;
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now - date;
  return diffMs < minutes * 60 * 1000;
};

/**
 * Format time for tooltip (full date and time)
 * @param {string} dateString - ISO date string
 * @returns {string} Full formatted date and time
 */
export const formatFullDateTime = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toLocaleString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
};
